import PowersportsAsset from '../../asset/powersports-asset';
import PowersportsCta from '../../cta/powersports-cta';
import Cta from '../../cta';
import Platform, { PageTypes, PlatformAssetWrapperMap } from '../../platform';
import { createStyleTag, StyleTag } from '../../util/create-style-tag';
// CSS Style files content - Each imported variable is holding the content of the file as a string
import srpStyleContent from './style/srp.css';
import vdpStyleContent from './style/vdp.css';
import AssetWrapper from '../../asset/wrappers';
import { filterObjectPropertiesByNamePrefixAndIndex, WrappedHTMLElement } from '../../util/parse-data';
import { PageNotReady } from '../../errors';
import { DSv6UtagData } from './typings';
import { jQueryEvent, OlWarning } from '../../events';

/**
 * Dealerspike V6 Vehicle Detail Page (VDP) Wrapper
 */
export class DealerspikeV6VdpAssetWrapper extends AssetWrapper<PowersportsAsset> {
  getDocumentStyles(): StyleTag[] {
    return [createStyleTag(vdpStyleContent.toString(), 'vdp-style')];
  }

  getRenderLocation(): HTMLElement {
    let targetLocation = this.container.querySelector<HTMLElement>('.ol-target-element');
    if (targetLocation) {
      return targetLocation;
    }

    const renderLocation = this.container.querySelector<HTMLElement>('.unitLeadButtons > ul');
    targetLocation = document.createElement('div');
    targetLocation.classList.add('ol-target-element');

    if (!renderLocation) {
      throw new PageNotReady('Target Render Location not found');
    }

    // Insert the CTA before the ul because some sites dynamically populate their buttons. Example:
    // https://www.michaelscycleworks.com/New-Inventory-2023-Kawasaki-Utility-Vehicle-Teryx-KRX-4-1000-eS-Michael-s-Cycle-Works-12505949?ref=list
    renderLocation.insertAdjacentElement('beforebegin', targetLocation);
    return targetLocation;
  }

  parseAssetData(): PowersportsAsset {
    /**
     * Helper function to get a text value from the DOM using the DSv6 unitHighlights table and LiInv* classes
     * @param selector
     */
    const getDomValue = (selector: string) =>
      new WrappedHTMLElement(this.container, selector).childBySelector('.spnUnitValue')?.getTextContent() || null;
    // Get object containing keys from `utag_data` starting with `product_`
    const assetUtagData: DSv6UtagData = filterObjectPropertiesByNamePrefixAndIndex(window.utag_data, 'product_');
    let price = null;
    if (assetUtagData['product_price']) {
      price = parseInt(assetUtagData['product_price']);
    }
    if (!price) {
      // not an else here in case we parsed a price of 0 from product_price
      // try to parse price from DOM. Will return null if any step fails
      price = new WrappedHTMLElement(this.container, '.unitPrice')
        ?.getTextContent()
        ?.matchOne(/\$[0-9,]+/)
        ?.toInt();
    }
    return new PowersportsAsset({
      // Data from utag_data
      category: assetUtagData['product_category'] || null,
      make: assetUtagData['product_make'] || null,
      model: assetUtagData['product_model'] || null,
      // on_sale is represented as a number, as a string
      on_sale: assetUtagData['product_on_sale'] ? parseInt(assetUtagData['product_on_sale']) !== 0 : false,
      price: price,
      id: assetUtagData['product_id'] || null,
      year: assetUtagData['product_year'] || null,

      // Data from DOM
      color: getDomValue('.LiInvColor')?.value,
      condition: assetUtagData['product_condition'] || getDomValue('.LiInvCondition')?.value,
      originalPrice: getDomValue('.LiInvMSRPPrice')?.toInt(),
      vin: getDomValue('.LiInvVIN')?.value,
      stockNumber: getDomValue('.LiInvStockNumber')?.value,
      subcategory: new WrappedHTMLElement(this.container, '.BodySubType').getTextContent()?.value,
      location: getDomValue('.LiInvLocation')?.value
    });
  }

  createCta(): Cta {
    return new PowersportsCta(this, { style: 'standard' });
  }

  static getPageContainer(): HTMLElement {
    const pageContainer = document.querySelector<HTMLElement>('#template');
    if (pageContainer) return pageContainer;
    else throw new Error('Page container not found.');
  }

  static shouldWatchElements(): boolean {
    return true;
  }
}

/**
 * Dealerspike V6 Search Results Page (SRP) Wrapper
 */
export class DealerspikeV6SrpAssetWrapper extends AssetWrapper<PowersportsAsset> {
  getDocumentStyles(): StyleTag[] {
    return [createStyleTag(srpStyleContent.toString(), 'srp-style')];
  }

  getRenderLocation(): HTMLElement {
    let targetLocation = this.container.querySelector<HTMLElement>('.ol-target-element');
    if (targetLocation) {
      return targetLocation;
    }

    const renderLocation = this.container.querySelector<HTMLElement>('.VehicleDetail');
    targetLocation = document.createElement('div');
    targetLocation.classList.add('ol-target-element');

    if (!renderLocation) {
      throw new PageNotReady('Target Render Location not found');
    }

    renderLocation.appendChild(targetLocation);
    return targetLocation;
  }

  parseAssetData(): PowersportsAsset {
    /**
     * Helper function to get a text value from the DOM using the DSv6 unitHighlights table and LiInv* classes
     * @param selector
     */
    // used to extract data from CSS classes
    const wrappedContainer = new WrappedHTMLElement(this.container);
    // used to extract data from unitSpecs
    const getDomValue = (selector: string) =>
      new WrappedHTMLElement(this.container, selector).childBySelector('.unitValue')?.getTextContent() || null;
    // Get object containing keys from `utag_data` starting with `product_`
    const assetUtagData: DSv6UtagData = filterObjectPropertiesByNamePrefixAndIndex(window.utag_data, 'product_');
    const parseIndex = this.parseIndex === null ? -1 : this.parseIndex;
    if (parseIndex < 0) {
      console.warn('parseIndex is null and cannot be used for data retrieval!');
    }

    function tryGetListData<T = string>(key: `product_list_${string}`): T | null {
      // Try to get list of values
      const values = assetUtagData[key] ? (assetUtagData[key] as Array<T>) : null;
      if (values && values[parseIndex] !== undefined) {
        // Return value at current parse index if found
        return values[parseIndex];
      }
      return null;
    }

    // parse price
    let price =
      new WrappedHTMLElement(this.container, '.InvPrice').getTextContent()?.toInt() ??
      new WrappedHTMLElement(this.container, '.NoBGInvPrice').getTextContent()?.toInt() ??
      null;
    if (!price && tryGetListData('product_list_prices'))
      price = parseInt(tryGetListData('product_list_prices') as string) || null;

    return new PowersportsAsset({
      price: price,
      vin: null, // not available

      // Data from utag_data
      id: tryGetListData('product_list_ids'),
      make: tryGetListData('product_list_makes') || wrappedContainer.searchClassListByPrefix('dspMake-')?.value,
      model: tryGetListData('product_list_names') || wrappedContainer.searchClassListByPrefix('dspModel-')?.value,
      year: tryGetListData('product_list_years') || wrappedContainer.searchClassListByPrefix('dspYear-')?.value,
      category:
        tryGetListData('product_list_categories') || wrappedContainer.searchClassListByPrefix('dspBodyType-')?.value,
      // on_sale is represented as a number, as a string
      on_sale: tryGetListData('product_list_on_sale')
        ? parseInt(tryGetListData('product_list_on_sale') as string) !== 0 // 1 is on sale, 0 is not
        : false,

      // Data from DOM
      originalPrice: new WrappedHTMLElement(this.container, '.InvRetPrc')?.getTextContent()?.toInt(),
      subcategory: wrappedContainer.searchClassListByPrefix('dspSubType-')?.value,
      stockNumber: getDomValue('.stockno')?.value,
      color: getDomValue('.color')?.value || wrappedContainer.searchClassListByPrefix('dspColor-')?.value,
      condition:
        getDomValue('.InvCondition')?.value || wrappedContainer.searchClassListByPrefix('dspCondition-')?.value,
      location: getDomValue('.VehicleLocation')?.value
    });
  }

  createCta(): Cta {
    return new PowersportsCta(this, { style: 'standard' });
  }

  static getPageContainer(): HTMLElement {
    const pageContainer = document.querySelector<HTMLElement>('#VehBody');
    if (pageContainer) return pageContainer;
    else throw new Error('Page container not found.');
  }

  static shouldWatchElements(): boolean {
    return true;
  }
}

/**
 * Dealerspike v6 platform class
 */
export default class DealerspikeV6 extends Platform {
  /**
   * Used to ensure special jQuery listeners are only initialized once
   */
  $jQueryListeners = false;
  pageTypeMap: PlatformAssetWrapperMap = {
    [PageTypes.SRP]: DealerspikeV6SrpAssetWrapper,
    [PageTypes.VDP]: DealerspikeV6VdpAssetWrapper
  };

  /**
   * Override watchElements to also watch for the renderComplete event,
   * which is a custom jQuery event triggered by DSv6.
   */
  watchElements() {
    // Still observe page with mutation observer
    super.watchElements();
    this.observePageChanges();

    if (!this.$jQueryListeners && this.pageType === PageTypes.SRP && window.jQuery) {
      // Ensure we only set up listeners once
      this.$jQueryListeners = true;
      try {
        window.jQuery('#VehBody').on('renderingComplete', ($event: JQuery.TriggeredEvent) => {
          console.debug('JQ render complete event, re-triggering render');
          // This call could get ignored if a render call is already taking place
          void this.render(new jQueryEvent({ detail: { $event } }));
        });
      } catch (error) {
        this.eventBus.dispatchEvent(
          new OlWarning({ detail: { error, msg: '[DSv6] Failed to setup renderingComplete handler' } })
        );
      }
    }
  }

  observePageChanges() {
    const debounce = (func: (...args: any[]) => void, wait: number) => {
      let timeout: ReturnType<typeof setTimeout>;
      return (...args: any[]) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    };
    const observer = new MutationObserver(
      debounce((mutations: MutationRecord[]) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'childList' && mutation.addedNodes.length) {
            console.debug('Detected new nodes added to SRP, re-initializing widget.');
            void this.render();
          }
        });
      }, 100)
    );

    if (this.pageType === PageTypes.SRP) {
      observer.observe(document.body, { childList: true, subtree: true });
    }
  }

  determinePageType(): PageTypes {
    // Check for SRP page
    if (document.querySelector('.DS1InventoryList')) {
      return PageTypes.SRP;
    }

    // Check for VDP page
    if (
      // Page type is inventory
      window.utag_data?.product_type === 'Inventory' &&
      // Buttons exist (our render location)
      document.querySelector('.unitLeadButtons')?.querySelector('ul') &&
      // Asset Container ID
      document.querySelector('#template') &&
      // Price / Quote element exists (for parsing price)
      document.querySelector('.unitLeadButtons')
    ) {
      return PageTypes.VDP;
    }

    // Wait until next cycle
    throw new PageNotReady('Page Type not yet determined.');
  }

  getAssetContainers(): HTMLElement[] {
    let containers;
    if (this.pageType === PageTypes.SRP) {
      containers = Array.from(window.document.querySelectorAll<HTMLElement>('.vehicle_row'));
    } else if (this.pageType === PageTypes.VDP) {
      containers = Array.from(document.querySelectorAll<HTMLElement>('#template'));
    } else {
      throw new Error('pageType is not supported by this platform');
    }

    if (!containers?.length) {
      throw new PageNotReady('Containers not yet found');
    }

    return containers;
  }
}
